import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isBugReportAdmin } from '../utils/bugReportAccess';
import { BugReport } from '@mui/icons-material';
import { toPng } from 'html-to-image';
import CursorTooltip from './CursorTooltip';
import PortalSnackbar from './PortalSnackbar';
import BugReportDialog from './BugReportDialog';

const SCREENSHOT_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

async function captureRootPng() {
  const node = document.getElementById('root');
  if (!node) throw new Error('Page root was not found');
  const dataUrl = await toPng(node, {
    pixelRatio: 1,
    // A cache-bust query string makes blob: URLs and some font files fail fetch,
    // which aborts the whole screenshot ("Failed to fetch").
    cacheBust: false,
    skipFonts: true,
    imagePlaceholder: SCREENSHOT_PLACEHOLDER,
    onImageErrorHandler: () => {},
    backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
    filter: (domNode) => {
      const classList = domNode?.classList;
      if (!classList) return true;
      if (classList.contains('app-cursor-tooltip')) return false;
      if (classList.contains('notification-bell-panel')) return false;
      if (classList.contains('bug-report-menu')) return false;
      // A PDF canvas drawn by the cross-origin worker can taint toDataURL and abort capture.
      if (classList.contains('react-pdf__Page__canvas')) return false;
      return true;
    }
  });
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], 'page-capture.png', { type: 'image/png' });
}

const BugReportButton = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewBoards = isBugReportAdmin(user);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const capturingRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureFile, setCaptureFile] = useState(null);
  const [captureError, setCaptureError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) {
      setMenuPosition(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const width = 220;
    let left = rect.right - width;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setMenuPosition({ top: rect.bottom + 8, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const startReport = async () => {
    if (capturingRef.current || dialogOpen) return;
    capturingRef.current = true;
    setCapturing(true);
    let file = null;
    let message = '';
    try {
      file = await captureRootPng();
    } catch {
      message = 'Automatic screenshot failed. You can paste or upload one instead.';
    }
    setCaptureFile(file);
    setCaptureError(message);
    capturingRef.current = false;
    setCapturing(false);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    setMenuOpen(false);
    window.setTimeout(() => {
      startReport();
    }, 0);
  };

  const handleViewReports = () => {
    setMenuOpen(false);
    navigate('/bug-reports');
  };

  return (
    <>
      <CursorTooltip title={capturing ? 'Capturing the page…' : 'Bug reports'} disabled={menuOpen || dialogOpen}>
        <button
          ref={buttonRef}
          type="button"
          className="header-notification-bell"
          aria-label="Bug reports"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-busy={capturing}
          onClick={() => setMenuOpen((prev) => !prev)}
          style={{ opacity: capturing ? 0.6 : 1 }}
        >
          <BugReport sx={{ fontSize: 22 }} />
        </button>
      </CursorTooltip>
      {menuOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="bug-report-menu"
          role="menu"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            zIndex: 12000
          }}
        >
          <button type="button" className="bug-report-menu__item" role="menuitem" onClick={handleSubmit}>
            Submit a report
          </button>
          <button type="button" className="bug-report-menu__item" role="menuitem" onClick={handleViewReports}>
            {canViewBoards ? 'View all reports' : 'View my open reports'}
          </button>
        </div>,
        document.body
      )}
      <BugReportDialog
        open={dialogOpen}
        captureFile={captureFile}
        captureError={captureError}
        onClose={() => setDialogOpen(false)}
        onSubmitted={() => {
          setDialogOpen(false);
          setSnackbar('Bug report sent.');
        }}
      />
      <PortalSnackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </>
  );
};

export default BugReportButton;
